import React from "react";
import "./entitydashboard.scss";
import { Link, useParams } from "react-router-dom";
import PermissionGate from "../../PermissionGate";

import { FEATURE_FLAGS, useFeatureFlag } from "~/config/ff";
import PropTypes from "prop-types";

/**
 * Sidebar component showing quick-action cards for the Entity Profile page.
 * Each card navigates to a sub-page or triggers an action.
 *
 * @param {boolean} hasEntityPlusAccess - Whether the current entity has Entity+ service
 * @param {boolean} hasPayrollAccess - Whether the current entity has Payroll service
 * @param {Function} [onDownloadEntityData] - Callback to open the export modal.
 *   Only passed for single-entity users who can't access the My Entities dashboard (GXM-1878).
 *   When undefined, the download card is not rendered.
 */
const QuickActions = ({
  hasEntityPlusAccess,
  hasPayrollAccess,
  onDownloadEntityData,
}) => {
  const { id } = useParams();
  const { value: isEntityCalendarEnabled } = useFeatureFlag(
    FEATURE_FLAGS.ENTITY_CALENDAR,
    false,
  );
  const { value: isDocumentStorageFeatureEnabled } = useFeatureFlag(
    FEATURE_FLAGS.DOCUMENT_STORAGE,
    false,
  );

  const { value: isUpdateActiveServiceFeatureEnabled } = useFeatureFlag(
    FEATURE_FLAGS.UPDATE_ACTIVE_SERVICES,
    false,
  );
  const { value: isEmployeeFeatureEnabled } = useFeatureFlag(
    FEATURE_FLAGS.EMPLOYEE_REGISTRY,
    false,
  );
  return (
    <div className="quick-actions-container">
      <div className="heading-container">
        <h4 className="heading">Quick Actions</h4>
        <p className="desc">What information are you looking for?</p>
      </div>
      <div className="cards-container">
        <div className="action-card">
          <Link className="quick-actions-link" to={`/entity/${id}/info`}>
            <div className="icon-container">
              <span className="pi pi-building"></span>
            </div>
            <p className="card-name">Core Entity Data</p>
          </Link>
        </div>
        <div className="action-card">
          <Link
            className="quick-actions-link"
            to={`/entity/${id}/appointments-ownership`}
          >
            <div className="icon-container">
              <span className="pi pi-chart-pie"></span>
            </div>
            <p className="card-name">Appointments & Ownership</p>
          </Link>
        </div>
        {isEmployeeFeatureEnabled && hasPayrollAccess && (
          <div className="action-card">
            <Link className="quick-actions-link" to={`/entity/${id}/employee`}>
              <div className="icon-container">
                <span className="pi pi-users"></span>
              </div>
              <p className="card-name">Employee Registry</p>
            </Link>
          </div>
        )}
        {isEntityCalendarEnabled && (
          <div className="action-card">
            <Link className="quick-actions-link" to={`/entity/${id}/calendar`}>
              <div className="icon-container">
                <span className="pi pi-calendar"></span>
              </div>
              <p className="card-name">Entity Calendar</p>
            </Link>
          </div>
        )}
        <div className="action-card">
          <Link
            className="quick-actions-link"
            to={`/entity/${id}/key-personnel`}
          >
            <div className="icon-container">
              <span className="pi pi-users "></span>
            </div>
            <p className="card-name">Key Personnel</p>
          </Link>
        </div>
        {isDocumentStorageFeatureEnabled && (
          <div className="action-card">
            <Link className="quick-actions-link" to={`/files/entity/${id}`}>
              <div className="icon-container">
                <span className="pi pi-folder "></span>
              </div>
              <p className="card-name">Files & Documents</p>
            </Link>
          </div>
        )}
        {isUpdateActiveServiceFeatureEnabled && (
          <PermissionGate
            resource="entity"
            action="modify"
            resourceAttr={{ entity_id: id }}
          >
            <div className="action-card">
              <Link
                className="quick-actions-link"
                to={`/entity/${id}/entity-settings/active-services`}
              >
                <div className="icon-container">
                  <span className="pi pi-cog"></span>
                </div>
                <p className="card-name">Entity Settings</p>
              </Link>
            </div>
          </PermissionGate>
        )}
        {/* Download card only shown for single-entity users (GXM-1878).
            Uses a <button> instead of <Link> since it opens a modal, not a route. */}
        {onDownloadEntityData && (
          <div className="action-card">
            <button
              type="button"
              className="quick-actions-link"
              onClick={onDownloadEntityData}
            >
              <div className="icon-container">
                <span className="pi pi-download"></span>
              </div>
              <p className="card-name">Download Entity Data</p>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

QuickActions.propTypes = {
  hasEntityPlusAccess: PropTypes.bool,
  hasPayrollAccess: PropTypes.bool,
  onDownloadEntityData: PropTypes.func,
};

export default QuickActions;
